"use client";

import ArrowLeftIcon from "@/assets/icons/arrowLeft.svg?react";
import ChevronRightIcon from "@/assets/icons/chevronRight.svg?react";
import { L0MenuItem, L2MenuItem } from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import Accordion from "@/components/ui/accordion";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { getLangFromLocale } from "@/utils/common";
// import Image from "next/image";
import Image from "@/components/ui/imageV2/ImageV2";
import { useParams } from "next/navigation";
import NoImageSrc from "@/assets/images/no_image.svg";
import { ReactNode, useState } from "react";

type SubEle = {
  id: string;
  title: string;
  subMenu: string[];
  link: string | null;
};

interface IProps {
  menu: L0MenuItem[];
  closeMenu?: () => void;
}

export const NavAccordianV2 = ({ menu, closeMenu }: IProps) => {
  menu = menu.filter((v) => v.title);

  // states
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState("");

  // hooks
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);

  // methods
  function handleOpenSubMenu() {
    setOpenSubMenu(true);
  }

  function handleCloseSubMenu() {
    setOpenSubMenu(false);
  }

  function handleSetIndex(path: string) {
    setSelectedIndex(path);
  }

  function handleLinkSelection() {
    closeMenu?.();
  }

  function handleGenLinks() {
    const [menuIdx, subMenuIdx] = selectedIndex.split("-");
    const menIdx = parseInt(menuIdx),
      subMenIdx = parseInt(subMenuIdx);
    const subMenu = (menu[menIdx]?.subMenu?.[subMenIdx]?.subMenu ?? []) as L2MenuItem[];

    const res: ReactNode[] = [];

    for (const menu of subMenu) {
      res.push(
        <CLink
          key={menu.key}
          href={menu.link?.url ?? "/"}
          className="text-xs"
          onClick={handleLinkSelection}
        >
          {lang === "en" ? menu.title.en : menu.title.ar}
        </CLink>,
      );
    }

    if (menu[menIdx]?.promotionalTilesLayout === "single") {
      for (const tile of menu[menIdx]?.promotionalTiles ?? []) {
        res.push(
          <CLink
            key={tile.key}
            href={tile.link?.url ?? "/"}
            className="flex flex-col gap-2"
            onClick={handleLinkSelection}
          >
            <Image
              width={335}
              height={379}
              src={
                lang === "en"
                  ? tile?.media.en
                    ? getCloudinaryURL(tile?.media.en)
                    : NoImageSrc
                  : tile?.media.ar
                    ? getCloudinaryURL(tile?.media.ar)
                    : NoImageSrc
              }
              alt="img"
            />
            <p className="text-sm font-medium">{lang === "en" ? tile.linkText?.en : tile.linkText?.ar}</p>
          </CLink>,
        );
      }
    }

    if (menu[menIdx]?.promotionalTilesLayout === "double") {
      for (const tile of menu[menIdx]?.promotionalTiles ?? []) {
        res.push(
          <CLink key={tile.key} href={tile.link?.url ?? "/"} className="flex flex-col gap-2">
            <Image
              width={335}
              height={379}
              src={
                lang === "en"
                  ? tile?.media.en
                    ? getCloudinaryURL(tile?.media.en)
                    : NoImageSrc
                  : tile?.media.ar
                    ? getCloudinaryURL(tile?.media.ar)
                    : NoImageSrc
              }
              alt="img"
            />
            <p className="text-sm font-medium">{lang === "en" ? tile.linkText?.en : tile.linkText?.ar}</p>
          </CLink>,
        );
      }
    }

    return res;
  }

  return (
    <div className="pb-4 text-black">
      <div className={`${openSubMenu ? "block" : "hidden"} text-black`}>
        <button
          onClick={handleCloseSubMenu}
          className="flex w-full items-center gap-2 border-b border-t px-2 py-4"
        >
          {lang === "ar" ? <ArrowLeftIcon className="rotate-180" /> : <ArrowLeftIcon />}
          <p className="text-sm font-semibold">{lang === "en" ? "Back" : "خلف"}</p>
        </button>

        <div className="mt-6 flex flex-col gap-9 px-2">{handleGenLinks()}</div>
      </div>

      <Accordion
        className={cn(openSubMenu ? "hidden" : "")}
        items={menu.map((m, i) => ({
          title: (
            <p
              style={{
                color: m.color ?? "inherit",
              }}
              className="font-medium"
            >
              {lang === "en" ? m.title.en : m.title.ar}
            </p>
          ),
          content: m.subMenu?.map((s, j) => (
            <button
              onClick={() => {
                handleOpenSubMenu();
                handleSetIndex(`${i}-${j}`);
              }}
              type="button"
              key={j}
              className="flex w-full items-center justify-between gap-4 px-4 py-5"
            >
              <p className="text-xs font-medium">{lang === "en" ? s.title.en : s.title.ar}</p>
              {lang === "ar" ? <ChevronRightIcon className="rotate-180" /> : <ChevronRightIcon />}
            </button>
          )),
        }))}
      />
    </div>
  );
};
